"use client";

import { useRef, useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  INPUT_MONO,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import type { HOOKES_LAW_CALCULATOR_STRINGS } from "@/components/tools/hookes-law-calculator";
import {
  carrySolved,
  DiagramFrame,
  type SolveFieldSpec,
  SolveGrid,
  unitOptions,
  WorkingLines,
} from "@/components/tools/physics-fields/shared";
import {
  ComboDiagram,
  HookeGraph,
  OscillationGraph,
} from "@/components/tools/physics-fields/spring-diagrams";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { formatSig } from "@/lib/tool-kit/physics-fields/format";
import { readQuantity } from "@/lib/tool-kit/physics-fields/input";
import {
  type Arrangement,
  type HookeTarget,
  type OscillatorTarget,
  type SpringError,
  solveCombination,
  solveHooke,
  solveOscillator,
} from "@/lib/tool-kit/physics-fields/spring";
import {
  comboWorking,
  hookeWorking,
  oscillatorWorking,
} from "@/lib/tool-kit/physics-fields/spring-working";
import {
  FORCE_UNITS,
  fromBase,
  LENGTH_UNITS,
  MASS_UNITS,
  STIFFNESS_UNITS,
  TIME_UNITS,
} from "@/lib/tool-kit/physics-fields/units";

type Strings = typeof HOOKES_LAW_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  sig: number;
}

function Failure({ error, strings }: { error: SpringError; strings: Strings }) {
  const messages: Record<SpringError, string> = {
    missing: strings.errorMissing,
    "non-positive": strings.errorPositive,
    "zero-extension": strings.errorZeroExtension,
    "sign-mismatch": strings.errorSign,
    "not-finite": strings.errorRange,
  };
  return error === "missing" ? (
    <Notice>{messages.missing}</Notice>
  ) : (
    <ErrorNotice>{messages[error]}</ErrorNotice>
  );
}

function springState(extension: number, strings: Strings): string {
  if (extension < 0) {
    return strings.compressed;
  }
  return extension === 0 ? strings.relaxed : strings.stretched;
}

function Results({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) {
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{title}</h2>
      {children}
    </section>
  );
}

/* ── F = kx ───────────────────────────────────────────────────────────────── */

type HookeKey = "F" | "k" | "x";

const HOOKE_EXAMPLES: Record<
  string,
  {
    text: Record<HookeKey, string>;
    unit: Record<HookeKey, string>;
    target: HookeTarget;
  }
> = {
  stretch: {
    text: { F: "", k: "200", x: "5" },
    unit: { F: "N", k: "N/m", x: "cm" },
    target: "force",
  },
  compress: {
    text: { F: "", k: "5", x: "-2" },
    unit: { F: "N", k: "kN/m", x: "cm" },
    target: "force",
  },
  measure: {
    text: { F: "9.80665", k: "", x: "4.9" },
    unit: { F: "N", k: "N/m", x: "cm" },
    target: "stiffness",
  },
};

export function HookePanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const start = HOOKE_EXAMPLES.stretch;
  const [example, setExample] = useState("stretch");
  const [target, setTarget] = useState<HookeTarget>(start.target);
  const [text, setText] = useState<Record<HookeKey, string>>(start.text);
  const [unit, setUnit] = useState<Record<HookeKey, string>>(start.unit);

  const outcome = solveHooke(
    {
      force: readQuantity(text.F, FORCE_UNITS, unit.F),
      stiffness: readQuantity(text.k, STIFFNESS_UNITS, unit.k),
      extension: readQuantity(text.x, LENGTH_UNITS, unit.x),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<HookeKey>[] = [
    {
      key: "F",
      label: strings.force,
      units: FORCE_UNITS,
      solved: target === "force",
    },
    {
      key: "k",
      label: strings.stiffness,
      units: STIFFNESS_UNITS,
      solved: target === "stiffness",
    },
    {
      key: "x",
      label: strings.extension,
      units: LENGTH_UNITS,
      solved: target === "extension",
      hint: strings.extensionHint,
    },
  ];
  const solvedSI = {
    F: value?.force ?? null,
    k: value?.stiffness ?? null,
    x: value?.extension ?? null,
  };
  const names = strings.hookeExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = HOOKE_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setTarget(ex.target);
    }
  };

  return (
    <>
      <SelectField
        id="hl-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(HOOKE_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="hl-solve"
        onChange={(next: HookeTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "force", label: strings.forceShort },
          { value: "stiffness", label: strings.stiffnessShort },
          { value: "extension", label: strings.extensionShort },
        ]}
        value={target}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="hl"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <Results title={strings.results}>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.appliedForce}
                value={`${formatSig(fmt, value.force, sig)} N`}
              />
              <StatCard
                label={strings.extension}
                value={`${formatSig(fmt, value.extension, sig)} m`}
              />
              <StatCard
                label={strings.energy}
                value={`${formatSig(fmt, value.energy, sig)} J`}
              />
              <StatCard
                label={strings.stiffness}
                value={`${formatSig(fmt, value.stiffness, sig)} N/m`}
              />
              <StatCard
                label={strings.restoring}
                value={`${formatSig(fmt, -value.force, sig)} N`}
              />
              <StatCard
                label={strings.state}
                value={springState(value.extension, strings)}
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={hookeWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.graphCaption}>
              <HookeGraph
                description={strings.graphDescription}
                energyText={strings.graphEnergy}
                extensionText={`x = ${formatSig(fmt, value.extension, sig)} m`}
                forceText={`F = ${formatSig(fmt, value.force, sig)} N`}
                id="hl-graph"
                label={strings.graphLabel}
                stretched={value.extension >= 0}
              />
            </DiagramFrame>
          </>
        )}
      </Results>
    </>
  );
}

/* ── Springs together ─────────────────────────────────────────────────────── */

interface Row {
  id: number;
  text: string;
}

const COMBO_EXAMPLES: Record<
  string,
  { texts: string[]; arrangement: Arrangement; load: string }
> = {
  two: { texts: ["100", "100"], arrangement: "series", load: "10" },
  three: { texts: ["100", "200", "300"], arrangement: "series", load: "12" },
};
const MAX_SPRINGS = 6;

export function ComboPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const nextId = useRef(10);
  const make = (texts: string[]): Row[] =>
    texts.map((text) => {
      nextId.current += 1;
      return { id: nextId.current, text };
    });
  const [example, setExample] = useState("two");
  const [rows, setRows] = useState<Row[]>(() =>
    COMBO_EXAMPLES.two.texts.map((text, index) => ({ id: index + 1, text }))
  );
  const [arrangement, setArrangement] = useState<Arrangement>("series");
  const [unit, setUnit] = useState("N/m");
  const [load, setLoad] = useState("10");
  const [loadUnit, setLoadUnit] = useState("N");

  const stiffnesses = rows.map((r) =>
    readQuantity(r.text, STIFFNESS_UNITS, unit)
  );
  const loadSI = readQuantity(load, FORCE_UNITS, loadUnit);
  const outcome = solveCombination(stiffnesses, arrangement, loadSI);
  const value = outcome.ok ? outcome.value : null;
  const names = strings.comboExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = COMBO_EXAMPLES[id];
    if (ex) {
      setRows(make(ex.texts));
      setArrangement(ex.arrangement);
      setLoad(ex.load);
      setUnit("N/m");
      setLoadUnit("N");
    }
  };
  const edit = (id: number, text: string) => {
    setExample("custom");
    setRows(rows.map((r) => (r.id === id ? { ...r, text } : r)));
  };

  return (
    <>
      <SelectField
        id="hl-combo-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(COMBO_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.arrangement}
        name="hl-arrangement"
        onChange={setArrangement}
        options={[
          { value: "series", label: strings.series },
          { value: "parallel", label: strings.parallel },
        ]}
        value={arrangement}
      />
      <div className="space-y-3">
        <p className={LABEL}>{strings.springsHeading}</p>
        {rows.map((row, index) => (
          <div
            className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2"
            key={row.id}
          >
            <div className="space-y-1">
              <label className={LABEL} htmlFor={`hl-spring-${row.id}`}>
                {t(strings.springLabel, { n: fmt(index + 1) })}
              </label>
              <input
                className={INPUT_MONO}
                id={`hl-spring-${row.id}`}
                inputMode="decimal"
                onChange={(event) => edit(row.id, event.target.value)}
                value={row.text}
              />
            </div>
            <button
              aria-label={t(strings.removeSpring, { n: fmt(index + 1) })}
              className={BUTTON_SMALL}
              disabled={rows.length <= 2}
              onClick={() => setRows(rows.filter((r) => r.id !== row.id))}
              type="button"
            >
              {strings.remove}
            </button>
          </div>
        ))}
        <div className="flex flex-wrap items-end gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={rows.length >= MAX_SPRINGS}
            onClick={() => setRows([...rows, ...make([""])])}
            type="button"
          >
            {strings.addSpring}
          </button>
          <div className="w-40">
            <SelectField
              id="hl-combo-unit"
              label={strings.stiffnessUnit}
              onChange={setUnit}
              options={unitOptions(STIFFNESS_UNITS)}
              value={unit}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_7rem] items-end gap-2 sm:max-w-md">
        <div className="space-y-1">
          <label className={LABEL} htmlFor="hl-load">
            {strings.load}
          </label>
          <input
            className={INPUT_MONO}
            id="hl-load"
            inputMode="decimal"
            onChange={(e) => setLoad(e.target.value)}
            value={load}
          />
        </div>
        <SelectField
          id="hl-load-unit"
          label={strings.loadUnit}
          onChange={setLoadUnit}
          options={unitOptions(FORCE_UNITS)}
          value={loadUnit}
        />
      </div>
      <Results title={strings.results}>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.equivalent}
                value={`${formatSig(fmt, value.stiffness, sig)} N/m`}
              />
              <StatCard
                label={strings.totalExtension}
                value={
                  value.extension === null
                    ? "—"
                    : `${formatSig(fmt, value.extension, sig)} m`
                }
              />
              <StatCard
                label={strings.totalEnergy}
                value={
                  value.energy === null
                    ? "—"
                    : `${formatSig(fmt, value.energy, sig)} J`
                }
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={comboWorking(
                value.rows.map((r) => r.stiffness),
                arrangement,
                value.stiffness,
                fmt,
                sig
              )}
            />
            {value.load === null ? null : (
              <DataTable
                caption={strings.shareCaption}
                head={[
                  strings.colSpring,
                  strings.colStiffness,
                  strings.colForce,
                  strings.colExtension,
                  strings.colEnergy,
                ]}
                rows={value.rows.map((r, i) => [
                  `k${["₁", "₂", "₃", "₄", "₅", "₆"][i] ?? ""}`,
                  `${formatSig(fmt, r.stiffness, sig)} N/m`,
                  `${formatSig(fmt, r.force, sig)} N`,
                  `${formatSig(fmt, r.extension, sig)} m`,
                  `${formatSig(fmt, r.energy, sig)} J`,
                ])}
              />
            )}
            <DiagramFrame
              caption={
                arrangement === "series"
                  ? strings.seriesCaption
                  : strings.parallelCaption
              }
            >
              <ComboDiagram
                arrangement={arrangement}
                count={rows.length}
                description={
                  arrangement === "series"
                    ? strings.seriesDescription
                    : strings.parallelDescription
                }
                equivalentText={`${arrangement === "series" ? "kₛ" : "kₚ"} = ${formatSig(fmt, value.stiffness, sig)} N/m`}
                id="hl-combo-diagram"
                label={strings.comboLabel}
              />
            </DiagramFrame>
          </>
        )}
      </Results>
    </>
  );
}

/* ── Mass on a spring ─────────────────────────────────────────────────────── */

type OscKey = "m" | "k" | "T" | "A";

const OSC_EXAMPLES: Record<
  string,
  { text: Record<OscKey, string>; unit: Record<OscKey, string> }
> = {
  hundred: {
    text: { m: "0.25", k: "100", T: "", A: "5" },
    unit: { m: "kg", k: "N/m", T: "s", A: "cm" },
  },
  eighty: {
    text: { m: "500", k: "80", T: "", A: "4" },
    unit: { m: "g", k: "N/m", T: "s", A: "cm" },
  },
};

export function OscillatorPanel({ strings, sig }: PanelProps) {
  const fmt = useToolNumber();
  const [example, setExample] = useState("hundred");
  const [target, setTarget] = useState<OscillatorTarget>("period");
  const [text, setText] = useState<Record<OscKey, string>>(
    OSC_EXAMPLES.hundred.text
  );
  const [unit, setUnit] = useState<Record<OscKey, string>>(
    OSC_EXAMPLES.hundred.unit
  );

  const outcome = solveOscillator(
    {
      mass: readQuantity(text.m, MASS_UNITS, unit.m),
      stiffness: readQuantity(text.k, STIFFNESS_UNITS, unit.k),
      period: readQuantity(text.T, TIME_UNITS, unit.T),
      amplitude: readQuantity(text.A, LENGTH_UNITS, unit.A),
    },
    target
  );
  const value = outcome.ok ? outcome.value : null;
  const specs: SolveFieldSpec<OscKey>[] = [
    {
      key: "m",
      label: strings.mass,
      units: MASS_UNITS,
      solved: target === "mass",
    },
    {
      key: "k",
      label: strings.stiffness,
      units: STIFFNESS_UNITS,
      solved: target === "stiffness",
    },
    {
      key: "T",
      label: strings.period,
      units: TIME_UNITS,
      solved: target === "period",
    },
    {
      key: "A",
      label: strings.amplitude,
      units: LENGTH_UNITS,
      solved: false,
      hint: strings.amplitudeHint,
    },
  ];
  const solvedSI = {
    m: value?.mass ?? null,
    k: value?.stiffness ?? null,
    T: value?.period ?? null,
  };
  const names = strings.oscillatorExamples as Record<string, string>;
  const pick = (id: string) => {
    setExample(id);
    const ex = OSC_EXAMPLES[id];
    if (ex) {
      setText(ex.text);
      setUnit(ex.unit);
      setTarget("period");
    }
  };

  return (
    <>
      <SelectField
        id="hl-osc-example"
        label={strings.exampleLabel}
        onChange={pick}
        options={[
          { value: "custom", label: strings.custom },
          ...Object.keys(OSC_EXAMPLES).map((id) => ({
            value: id,
            label: names[id] ?? id,
          })),
        ]}
        value={example}
      />
      <PillGroup
        label={strings.solveFor}
        name="hl-osc-solve"
        onChange={(next: OscillatorTarget) => {
          setText(carrySolved(specs, text, unit, solvedSI, sig));
          setTarget(next);
        }}
        options={[
          { value: "period", label: strings.periodShort },
          { value: "mass", label: strings.massShort },
          { value: "stiffness", label: strings.stiffnessShort },
        ]}
        value={target}
      />
      <SolveGrid
        fmt={fmt}
        idPrefix="hl-osc"
        onText={(key, v) => {
          setExample("custom");
          setText({ ...text, [key]: v });
        }}
        onUnit={(key, v) => setUnit({ ...unit, [key]: v })}
        sig={sig}
        solvedSI={solvedSI}
        specs={specs}
        text={text}
        unit={unit}
        unitTemplate={strings.unitFor}
      />
      <Results title={strings.results}>
        {value === null ? (
          <Failure
            error={outcome.ok ? "missing" : outcome.error}
            strings={strings}
          />
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.period}
                value={`${formatSig(fmt, value.period, sig)} s`}
              />
              <StatCard
                label={strings.frequency}
                value={`${formatSig(fmt, value.frequency, sig)} Hz`}
              />
              <StatCard
                label={strings.angularFrequency}
                value={`${formatSig(fmt, value.angularFrequency, sig)} rad/s`}
              />
              <StatCard
                label={strings.maxSpeed}
                value={
                  value.maxSpeed === null
                    ? "—"
                    : `${formatSig(fmt, value.maxSpeed, sig)} m/s`
                }
              />
              <StatCard
                label={strings.maxAcceleration}
                value={
                  value.maxAcceleration === null
                    ? "—"
                    : `${formatSig(fmt, value.maxAcceleration, sig)} m/s²`
                }
              />
              <StatCard
                label={strings.oscillationEnergy}
                value={
                  value.energy === null
                    ? "—"
                    : `${formatSig(fmt, value.energy, sig)} J`
                }
              />
            </StatGrid>
            <WorkingLines
              heading={strings.working}
              lines={oscillatorWorking(target, value, fmt, sig)}
            />
            <DiagramFrame caption={strings.oscillationCaption}>
              <OscillationGraph
                amplitudeText={
                  value.amplitude === null
                    ? "A"
                    : `A = ${formatSig(fmt, fromBase(LENGTH_UNITS, "m", value.amplitude), sig)} m`
                }
                description={strings.oscillationDescription}
                id="hl-osc-graph"
                label={strings.oscillationLabel}
                periodText={`T = ${formatSig(fmt, value.period, sig)} s`}
              />
            </DiagramFrame>
          </>
        )}
      </Results>
    </>
  );
}
